import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Lock } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PRODUCTS } from "@/lib/products";
import { DSA_DISCLAIMER, FALLBACK_IDENTITY } from "@/lib/content";
import { fetchPages, fetchSiteIdentity, useWP } from "@/lib/wp";
import { GoogleReviewLink, TrustBoxMini } from "@/components/Marketing";

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

const ThreadsIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a14 14 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32l-1.757-1.18c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388q.163.07.321.142c1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69q-.362 0-.739.021c-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221" />
  </svg>
);

export const Footer = () => {
  const identity = useWP(fetchSiteIdentity, FALLBACK_IDENTITY, []);
  const pages = useWP(fetchPages, [], []);
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-concordia-ink text-ivory">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo dark icon={identity.icon} name={identity.name} />
            {identity.tagline && (
              <p className="mt-5 max-w-sm font-serif text-xl italic text-ivory/70">{identity.tagline}</p>
            )}
            <div className="mt-5 h-px w-12 bg-brass" aria-hidden="true" />
            <p className="mt-5 max-w-md text-xs leading-relaxed text-ivory/50">{DSA_DISCLAIMER}</p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.instagram.com/loan_salaah/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                aria-label="Loan Salaah on Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20 text-ivory/70 transition-all duration-300 hover:border-brass hover:text-brass"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/loan-salaah/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin-link"
                aria-label="Loan Salaah on LinkedIn"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20 text-ivory/70 transition-all duration-300 hover:border-brass hover:text-brass"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/loansalaah/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                aria-label="Loan Salaah on Facebook"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20 text-ivory/70 transition-all duration-300 hover:border-brass hover:text-brass"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://x.com/Loan_Salaah"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-x-link"
                aria-label="Loan Salaah on X"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20 text-ivory/70 transition-all duration-300 hover:border-brass hover:text-brass"
              >
                <XIcon className="h-4 w-4" />
              </a>
              <a
                href="https://www.threads.com/@loan_salaah"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-threads-link"
                aria-label="Loan Salaah on Threads"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20 text-ivory/70 transition-all duration-300 hover:border-brass hover:text-brass"
              >
                <ThreadsIcon className="h-4 w-4" />
              </a>
            </div>
            <TrustBoxMini />
            <GoogleReviewLink />
          </div>

          <div className="lg:col-span-3">
            <p className="text-[0.62rem] font-semibold uppercase tracking-[0.3em] text-brass">Loans</p>
            <ul className="mt-5 space-y-3">
              {PRODUCTS.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/loans/${p.slug}`}
                    data-testid={`footer-loan-${p.slug}`}
                    className="text-sm text-ivory/70 transition-colors hover:text-ivory"
                  >
                    {p.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="text-[0.62rem] font-semibold uppercase tracking-[0.3em] text-brass">Explore</p>
            <ul className="mt-5 space-y-3">
              <li><Link to="/about" data-testid="footer-about-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">About us</Link></li>
              <li><Link to="/partners" data-testid="footer-partners-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Our Partners</Link></li>
              <li><Link to="/card-finder" data-testid="footer-card-finder-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Card Finder</Link></li>
              <li><Link to="/account" data-testid="footer-account-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">My Account</Link></li>
              <li><Link to="/emi-calculator" data-testid="footer-emi-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">EMI Calculator</Link></li>
              <li><Link to="/savings-calculator" data-testid="footer-savings-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Savings Calculator</Link></li>
              <li><Link to="/eligibility-calculator" data-testid="footer-eligibility-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Eligibility Calculator</Link></li>
              <li><Link to="/prepayment-calculator" data-testid="footer-prepayment-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Prepayment Calculator</Link></li>
              <li><Link to="/loan-comparison" data-testid="footer-compare-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Compare Loans</Link></li>
              <li><Link to="/blog" data-testid="footer-blog-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Blog</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Contact</Link></li>
              {pages.slice(0, 5).map((pg) => (
                <li key={pg.slug}>
                  <Link to={`/p/${pg.slug}`} data-testid={`footer-page-${pg.slug}`} className="text-sm text-ivory/70 transition-colors hover:text-ivory">
                    {pg.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="text-[0.62rem] font-semibold uppercase tracking-[0.3em] text-brass">Legal</p>
            <ul className="mt-5 space-y-3">
              <li><Link to="/legal/privacy-policy" data-testid="footer-privacy-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Privacy Policy</Link></li>
              <li><Link to="/legal/terms-of-use" data-testid="footer-terms-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Terms of Use</Link></li>
              <li><Link to="/legal/disclaimer" data-testid="footer-disclaimer-link" className="text-sm text-ivory/70 transition-colors hover:text-ivory">Disclaimer</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-ivory/10 pt-6 lg:flex-row lg:items-center">
          <p className="text-xs text-ivory/45" data-testid="footer-brand-credit">
            © {year} {identity.name} — a brand of Akhyata Advisory LLP. All rights reserved.
          </p>
          <p className="flex items-center gap-1.5 text-xs text-ivory/45" data-testid="footer-ssl-badge">
            <Lock className="h-3.5 w-3.5 text-brass" /> 256-bit SSL Encrypted
          </p>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-ivory/35">
            DSA · Loan Facilitator · Marketplace
          </p>
          <p className="text-xs text-ivory/45">
            Website designed by{" "}
            <a
              href="https://www.demandandco.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-designer-link"
              className="font-semibold text-ivory/70 underline decoration-brass/50 underline-offset-2 transition-colors hover:text-brass"
            >
              Demand&amp;Co.
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
